import { createContext, useCallback, useEffect, type ReactNode } from 'react';
import { darkTokens, lightTokens } from './tokens';
import type { ThemeName } from './tokens';
import { useSettingsStore } from '../stores/settingsStore';

export type ThemeContextValue = {
  theme: ThemeName;
  toggle: () => void;
};

export const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  toggle: () => {},
});

function applyTokens(theme: ThemeName): void {
  const tokens = theme === 'dark' ? darkTokens : lightTokens;
  const root = document.documentElement;

  for (const [key, value] of Object.entries(tokens)) {
    root.style.setProperty(key, value);
  }

  root.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useSettingsStore((s) => s.theme);
  const setTheme = useSettingsStore((s) => s.setTheme);

  useEffect(() => {
    applyTokens(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}
